import type { EditorView } from "prosemirror-view";
import type { NLSDocument } from "../../documents/types.ts";
import type { ViewspecState } from "../../viewspecs/types.ts";

export interface CommandArg {
  name: string;
  type: "sid" | "text" | "viewspec" | "document" | "confirm";
  prompt: string;
  optional?: boolean;
}

export interface CommandContext {
  editorView: EditorView | null;
  currentDocument: NLSDocument | null;
  navigate: (path: string) => void;
  setViewspecs: (vs: Partial<ViewspecState>) => void;
  focusEditor: () => void;
}

export interface NLSCommand {
  name: string;
  aliases: string[];
  description: string;
  args: CommandArg[];
  execute: (ctx: CommandContext, args: Record<string, string>) => void;
}

const commands: NLSCommand[] = [];

export function registerCommand(cmd: NLSCommand): void {
  commands.push(cmd);
}

export function getAllCommands(): NLSCommand[] {
  return [...commands];
}

/**
 * Find a command by name or alias.
 */
export function findCommand(input: string): NLSCommand | undefined {
  const lower = input.toLowerCase().trim();
  return commands.find(
    (cmd) =>
      cmd.name.toLowerCase() === lower ||
      cmd.aliases.some((a) => a.toLowerCase() === lower)
  );
}

/**
 * Fuzzy search commands by name/alias prefix.
 */
export function searchCommands(query: string): NLSCommand[] {
  const lower = query.toLowerCase().trim();
  if (!lower) return commands;
  return commands.filter(
    (cmd) =>
      cmd.name.toLowerCase().startsWith(lower) ||
      cmd.aliases.some((a) => a.toLowerCase().startsWith(lower))
  );
}
